import httpClient from '@/api/httpClient';
import { setLocalStorge } from '@/helpers/localStorage';
import { AuthenticationResponse } from '@/domains/authentication/types/authTypes';
import { getPartnerInformation } from '@/domains/profile/api/profile';
import { bootIntercom, getIntercomLifecycleVersion, shutdownIntercom } from '@/helpers/intercom';
import store from '@/store';

export const bootIntercomAfterOtl = async (): Promise<boolean> => {
  const lifecycleVersion = getIntercomLifecycleVersion();
  const profileResponse = await getPartnerInformation();
  if (!profileResponse?.partner) return false;

  return bootIntercom(profileResponse.partner, true, lifecycleVersion);
};

/**
 * Checks if the current URL has an `?otl=` query parameter.
 * If present, exchanges it for a full partner session via the
 * partner-login-by-otl endpoint, stores the result in localStorage
 * and Vuex, then strips the param from the URL.
 *
 * Must be called before the authentication guard so the session
 * is available when `isPartnerAuthenticatedLocally()` runs.
 */
export const consumeOtlIfPresent = async (): Promise<boolean> => {
  const params = new URLSearchParams(window.location.search);
  const otl = params.get('otl');

  if (!otl) return false;

  shutdownIntercom();

  // Strip immediately to prevent re-consumption on navigation/reload.
  params.delete('otl');
  const remaining = params.toString();
  const newUrl = window.location.pathname + (remaining ? `?${remaining}` : '') + window.location.hash;
  window.history.replaceState(null, '', newUrl);

  try {
    // The default httpClient will skip the token header if no valid session exists.
    const response: any = await httpClient.post('market/partner-login-by-otl', { token: otl });

    const partner = response?.partner;
    if (!partner?.['x-partner-token']) return false;

    // Persist to localStorage (same as "remember me" login flow).
    setLocalStorge(AuthenticationResponse.PartnerID, partner.id);
    setLocalStorge(AuthenticationResponse.Name, partner.name);
    setLocalStorge(AuthenticationResponse.Email, partner.email);
    setLocalStorge(AuthenticationResponse.Role, partner.role);
    setLocalStorge(AuthenticationResponse.PartnerToken, partner['x-partner-token']);
    setLocalStorge(AuthenticationResponse.Token_expiry_date, partner.token_expiry_date);
    setLocalStorge(AuthenticationResponse.AppsTypes, JSON.stringify(partner.apps_types ?? []));
    setLocalStorge(AuthenticationResponse.IsAccountOwner, partner.is_account_owner ?? false);

    // Update Vuex store so guards see the user as authenticated.
    store.dispatch('setPartnerID', partner.id);
    store.dispatch('authentication/setPartnerName', partner.name);
    store.dispatch('authentication/setPartnerEmail', partner.email);
    store.dispatch('authentication/setPartnerRole', partner.role ?? null);
    store.dispatch('authentication/setXPartnerTokenState', partner['x-partner-token']);
    store.dispatch('authentication/setTokenExpirationDate', partner.token_expiry_date);
    store.dispatch('authentication/setPartnerAppsTypes', partner.apps_types ?? []);
    store.dispatch('authentication/setPartnerAuthenticated', true);
    store.dispatch('authentication/setIsAccountOwner', partner.is_account_owner ?? false);
    if (partner.role === null) await bootIntercomAfterOtl();
    return true;
  } catch {
    // OTL invalid or expired — user will be redirected to login by normal auth flow.
    return false;
  }
};
